import { TextFieldVariants, Typography, TypographyProps } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import React from 'react';

import { appConfig } from '../config/runtimeConfig';

import { MONTSERRAT_STACK } from './fonts';
// Ensure TypeScript loads our MUI module augmentation for the palette
import type {} from '@mui/material/styles';

const ellipsis = appConfig.APP_BRAND === 'ellipsis';

// Palette augmentation moved to src/theme/mui-augmentations.d.ts

export const theme = createTheme({
  palette: {
    primary: {
      main: ellipsis ? '#146A8C' : '#0dab96',
      light: ellipsis ? '#3f92b3' : '#96fffd',
      dark: ellipsis ? '#0c3749' : '#006c5a',
    },
    secondary: {
      main: ellipsis ? '#1c9ed9' : '#de5948',
      light: ellipsis ? '#0AB6FF' : '#ffb198',
      dark: ellipsis ? '#0081B8' : '#bf3d31',
    },
    // New offer palette used for discount/urgency banner
    offer: {
      // Accessible vivid red distinct from error
      // Adjust via theme if brand requires
      main: '#D62D20',
      contrastText: '#ffffff',
    } as any,
    error: {
      light: '#dc004e',
      main: '#f44336',
      dark: '#C5192D',
    },
    warning: {
      main: '#FD9D24',
    },
    info: {
      main: '#19486A',
    },
    success: {
      main: '#3F7E44',
    },
  } as any,
  spacing: 4,
  // No `fontFamily` here: MUI only applies its per-variant letterSpacing when
  // typography.fontFamily is exactly its default Roboto stack, so setting any
  // other value (even one that just adds a fallback) silently drops the
  // letter-spacing from every text style.
  typography: {
    allVariants: {
      color: '#555',
    },
    h1: {
      fontWeight: 'normal',
      fontSize: '2rem',
    },
    h2: {
      fontSize: '1.5rem',
    },
    h3: {
      fontSize: '1.4rem',
    },
    h4: {
      fontSize: '1.3rem',
    },
    h5: {
      fontSize: '1.25rem',
    },
    h6: {
      fontSize: '1.125rem',
    },
    subtitle1: {
      fontWeight: 900,
    },
    subtitle2: {
      color: '#9e9e9e',
      fontWeight: 400,
    },
    body1: {
      fontSize: 16,
      lineHeight: '1.2em',
      color: '#000',
    },
    body2: {
      fontSize: 12,
      lineHeight: '1em',
      color: '#000',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingTop: 'env(safe-area-inset-top, 20px)',
          [theme.breakpoints.down('sm')]: {
            height: styles.mobileAppBarHeight,
          },
          [theme.breakpoints.up('sm')]: {
            height: styles.appBarHeight,
          },
          padding: theme.spacing(4),
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            padding: styles.mediumCardPadding,
          },
          [theme.breakpoints.up('md')]: {
            padding: styles.mediumCardPadding,
          },
          [theme.breakpoints.up('lg')]: {
            padding: styles.largeCardPadding,
          },
        }),
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          border: '0px',
          borderRadius: '5px',
          '&:hover': {
            border: '0px',
          },
          '&::before': {
            border: '0px',
          },
          '&::after': {
            border: '0px',
          },
          '&:hover:not(.Mui-disabled):before': {
            border: '0px',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: `${theme.palette.error.light} !important`,
          marginRight: '1rem',
          marginLeft: '1rem',
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: ({ theme, ownerState }) => ({
          fontFamily: MONTSERRAT_STACK,
          color: '#fff',
          ...(ownerState.color === 'error' && {
            backgroundColor: theme.palette.error.main,
            '&:hover': {
              backgroundColor: theme.palette.error.light,
            },
          }),
          borderRadius: '50px',
          fontSize: '16px',
          lineHeight: '16px',
          fontWeight: 400,
          padding: '15px 25px 15px 25px',
          textTransform: 'capitalize',
          margin: 'auto',
        }),
        text: ({ theme, ownerState }) => ({
          fontFamily: MONTSERRAT_STACK,
          backgroundColor: 'transparent',
          color: '#01173F',
          '&:hover': {
            backgroundColor: 'transparent',
            color: '#009fe3',
          },
          ...(ownerState.color === 'error' && {
            color: theme.palette.error.main,
            '&:hover': {
              backgroundColor: 'transparent',
              color: theme.palette.error.dark,
            },
          }),
          fontSize: '16px',
          fontWeight: 400,
          textTransform: 'capitalize',
        }),
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
          cursor: 'pointer',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          span: {
            margin: 0,
          },
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        root: {
          color: '#14b57b',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 800,
      md: 1000,
      lg: 1200,
      xl: 1920,
    },
  },
});

export const styles = {
  appBarHeight: 90,
  mobileAppBarHeight: 60,
  bottomBarHeight: 60,
  textFieldVariant: 'outlined' as TextFieldVariants,
  largePagePadding: 50,
  mediumPagePadding: 20,
  smallPagePadding: 10,
  mobilePadding: 2,
  largeCardPadding: 20,
  mediumCardPadding: 10,
  smallCardPadding: 4,
  maxWidth: 2520,
  bottomBookingHeight: 90,
};

export const formatCurrency = (
  price = 0,
  currencyCode = 'eur',
  options: Intl.NumberFormatOptions = {}
) => {
  // `navigator` is undefined during SSR (Vike/Node) — this runs synchronously
  // in the render path (not an effect), so it must not assume a browser.
  const locale =
    typeof navigator !== 'undefined' ? navigator.languages[0] : undefined;
  try {
    return new Intl.NumberFormat(locale ?? 'en-GB', {
      style: 'currency',
      currency: currencyCode,
      ...options,
    }).format(price);
  } catch (e: unknown) {
    console.error(e);
    return `${currencyCode}${price}`;
  }
};

export const BodyText = (props: TypographyProps) => {
  const newSx = { marginTop: '15px', ...props.sx };
  return (
    <Typography variant="body1" sx={newSx}>
      {props.children}
    </Typography>
  );
};

export const Subheading = (props: TypographyProps) => {
  const newSx = { marginTop: '15px', ...props.sx };
  return (
    <Typography variant="h2" sx={newSx}>
      {props.children}
    </Typography>
  );
};

const formatStringToCamelCase = (str: string) => {
  const splitStr = str.split('-');
  if (splitStr.length === 1) return splitStr[0];
  return (
    splitStr[0] +
    splitStr
      .slice(1)
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join('')
  );
};

export const getStyleObjectFromString = (styleStr: string) => {
  const style: { [key: string]: string } = {};
  styleStr.split(';').forEach((el) => {
    const [property, value] = el.split(':');
    if (!property) return;

    const formattedProperty = formatStringToCamelCase(property.trim());
    style[formattedProperty] = value && value.trim();
  });

  return style;
};

export function getDynamicBranding() {
  // Ellipsis logos go through the CDN's image handler as WebP (keeps the
  // alpha channel, ~half the bytes of the stored PNGs).
  const logo =
    appConfig.APP_BRAND === 'ellipsis'
      ? `${appConfig.APP_CDN_BASE_URL}/0x150/filters:format(webp)/assets/logo.png`
      : '/hireaboat.png';
  // Intrinsic dimensions of the file each `logo*` URL above resolves to, at
  // its full/native resolution regardless of any CSS the header renders it
  // at. Lighthouse flags <img> elements with no explicit width/height as a
  // layout-shift risk; passing these through as HTML attributes lets the
  // browser reserve the right box before the image loads, while the CSS
  // height set where each is rendered still governs the displayed size.
  const logoWidth = appConfig.APP_BRAND === 'ellipsis' ? 215 : 600;
  const logoHeight = appConfig.APP_BRAND === 'ellipsis' ? 150 : 600;

  const logoLarge =
    appConfig.APP_BRAND === 'ellipsis'
      ? `${appConfig.APP_CDN_BASE_URL}/0x500/filters:format(webp)/assets/logo.png`
      : '/hireaboat.png';
  const logoLargeWidth = appConfig.APP_BRAND === 'ellipsis' ? 717 : 600;
  const logoLargeHeight = appConfig.APP_BRAND === 'ellipsis' ? 500 : 600;

  const mobileLogo =
    appConfig.APP_BRAND === 'ellipsis'
      ? `${appConfig.APP_CDN_BASE_URL}/0x150/filters:format(webp)/assets/mobileLogo.png`
      : '/hireaboat.png';
  const mobileLogoWidth = appConfig.APP_BRAND === 'ellipsis' ? 866 : 600;
  const mobileLogoHeight = appConfig.APP_BRAND === 'ellipsis' ? 150 : 600;

  const brand =
    appConfig.APP_BRAND === 'ellipsis' ? 'Ellipsis' : 'Hireaboat.co.za';

  const slug = appConfig.APP_BRAND === 'ellipsis' ? 'Seychelles' : 'za';

  const site =
    appConfig.APP_BRAND === 'ellipsis'
      ? 'www.ellipsis.sc'
      : 'www.hireaboat.co.za';

  const tagline =
    appConfig.APP_BRAND === 'ellipsis'
      ? 'Book your island dream with Ellipsis Marine'
      : 'Your Holiday Simplified';

  const country =
    appConfig.APP_BRAND === 'ellipsis' ? 'Seychelles' : 'South Africa';

  return {
    site,
    logo,
    logoWidth,
    logoHeight,
    logoLarge,
    logoLargeWidth,
    logoLargeHeight,
    mobileLogo,
    mobileLogoWidth,
    mobileLogoHeight,
    country,
    brand,
    slug,
    tagline,
  };
}
